import React, { useEffect, useState } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Card,
  Grid,
  Tabs,
  Tab,
  Stack,
  Container,
  Skeleton,
} from "@mui/material";
import SignupTrendsChart from "../../../components/admin-components/SignupTrendsChart";
import VolunteersTab from "../../../components/admin-components/VolunteersTab";
import VotersTab from "../../../components/admin-components/VotersTab";
import DonationsTab from "../../../components/admin-components/DonationsTab";
import EventsTab from "../../../components/admin-components/events-tab/EventsTab";
import NewsTab from "../../../components/admin-components/news-tab/NewsTab";
import VoteInfoTab from "../../../components/admin-components/voteInfo-tab/VoteInfoTab";
import FamilyStoriesTab from "../../../components/admin-components/FamilyStoriesTab";
import PhotosTab from "../../../components/admin-components/photos-tab/PhotosTab";
import EmailSignUpsTab from "../../../components/admin-components/emailSignUps-tab/EmailSignUpsTab";
import SurveysTab from "../../../components/admin-components/surveys-tab/SurveysTab";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../../../components/admin-components/reuseable-components/ConfirmModal";
import {
  getDashboardCounts,
  getEmailSignups,
  getVolunteers,
} from "../../../apis/admin-apis/dashboard-apis/DashboardApis";
import { getEvents } from "../../../apis/admin-apis/dashboard-apis/EventTabApis";
import { getNewsArticles } from "../../../apis/admin-apis/dashboard-apis/NewsTabApis";
import {
  getVoters,
  getVoteInfoBoxes,
} from "../../../apis/admin-apis/dashboard-apis/VoteInfoTabApis";
import { getStories } from "../../../apis/admin-apis/dashboard-apis/FamilyStoriesTabApis";
import { getDonations } from "../../../apis/admin-apis/dashboard-apis/DonationApis";
import { getGalleryCounts } from "../../../apis/admin-apis/dashboard-apis/PhotoApis";

const Dashboard = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [opendialog, setOpenDialog] = useState(false);
  const [isCountsLoading, setIsCountsLoading] = useState(true);
  const [isEmailSignupsLoading, setIsEmailSignupsLoading] = useState(true);
  const [volunteers, setVolunteers] = useState([]);
  const [voters, setVoters] = useState([]);
  const [events, setEvents] = useState([]);
  const [newsArticles, setNewsArticles] = useState([]);
  const [voteInfoBoxes, setVoteInfoBoxes] = useState([]);
  const [familyStories, setFamilyStories] = useState([]);
  const [donations, setDonations] = useState([]);
  const [donationSummary, setDonationSummary] = useState({
    totalRaised: 0,
    count: 0,
    uniqueDonors: 0,
    avgGift: 0,
  });
  const [photoCount, setPhotoCount] = useState(0);
  const [dashboardCounts, setDashboardCounts] = useState({
    voters: 0,
    emailSignups: 0,
    volunteers: 0,
    petitionSignatures: 0,
    events: 0,
    rsvps: 0,
    news: 0,
    donations: 0,
    total: 0,
  });
  const [emailSignups, setEmailSignups] = useState([]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("userDetails");
    navigate("/admin/login");
  };

  const handleOpenModal = () => {
    setOpenDialog(true);
  };

  const fetchDonations = async () => {
    try {
      const response = await getDonations("all");
      if (response?.success) {
        setDonations(response.donations || []);
        setDonationSummary(
          response.summary || {
            totalRaised: 0,
            count: 0,
            uniqueDonors: 0,
            avgGift: 0,
          },
        );
      }
    } catch (error) {
      console.error("Failed to load donations:", error);
    }
  };

  useEffect(() => {
    fetchDonations();
  }, []);

  const fetchPhotoCount = async () => {
    try {
      const response = await getGalleryCounts();
      if (response?.success) {
        const total = Object.values(response.counts || {}).reduce(
          (a, b) => a + b,
          0,
        );
        setPhotoCount(total);
      }
    } catch (error) {
      console.error("Failed to load photo counts:", error);
    }
  };

  useEffect(() => {
    fetchPhotoCount();
  }, []);

  const fetchDashboardCounts = async () => {
    try {
      setIsCountsLoading(true);

      const response = await getDashboardCounts();

      if (response?.success) {
        setDashboardCounts({
          ...response.counts,
          total: response.total ?? 0,
        });
      }
    } catch (error) {
      console.error("Failed to load dashboard counts:", error);
    } finally {
      setIsCountsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardCounts();
  }, []);

  const fetchEmailSignups = async () => {
    try {
      setIsEmailSignupsLoading(true);

      const response = await getEmailSignups();

      if (response?.success) {
        setEmailSignups(response.signups || []);
      }
    } catch (error) {
      console.error("Failed to load email signups:", error);
    } finally {
      setIsEmailSignupsLoading(false);
    }
  };

  useEffect(() => {
    fetchEmailSignups();
  }, []);

  const fetchVolunteers = async () => {
    try {
      const response = await getVolunteers();

      if (response?.success) {
        setVolunteers(response.applications || []);
      }
    } catch (error) {
      console.error("Failed to load volunteers:", error);
    }
  };

  useEffect(() => {
    fetchVolunteers();
  }, []);

  const fetchVoters = async () => {
    try {
      const response = await getVoters();

      if (response?.success) {
        setVoters(response.voters || []);
      }
    } catch (error) {
      console.error("Failed to load voters:", error);
    }
  };

  useEffect(() => {
    fetchVoters();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await getEvents();

      if (response?.success) {
        setEvents(response.events || []);
      }
    } catch (error) {
      console.error("Failed to load events:", error);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchVoteInfoBoxes = async () => {
    try {
      const response = await getVoteInfoBoxes();

      if (response?.success) {
        setVoteInfoBoxes(response.boxes || response.voteInfoBoxes || []);
      }
    } catch (error) {
      console.error("Failed to load vote info boxes:", error);
    }
  };

  useEffect(() => {
    fetchVoteInfoBoxes();
  }, []);

  const fetchNewsArticles = async () => {
    try {
      const response = await getNewsArticles();

      if (response?.success) {
        setNewsArticles(response.articles || []);
      }
    } catch (error) {
      console.error("Failed to load news articles:", error);
    }
  };

  useEffect(() => {
    fetchNewsArticles();
  }, []);

  const fetchFamilyStories = async () => {
    try {
      const response = await getStories();

      if (response?.success) {
        setFamilyStories(response.stories || []);
      }
    } catch (error) {
      console.error("Failed to load family stories:", error);
    }
  };

  useEffect(() => {
    fetchFamilyStories();
  }, []);

  const refreshDashboard = () => {
    fetchDashboardCounts();
    fetchEmailSignups();
    fetchVolunteers();
    fetchVoters();
    fetchEvents();
    fetchVoteInfoBoxes();
    fetchNewsArticles();
    fetchFamilyStories();
    fetchDonations();
    fetchPhotoCount();
  };

  const metrics = [
    { label: "TOTAL VOTERS", value: dashboardCounts.voters },
    { label: "EMAIL SIGNUPS", value: dashboardCounts.emailSignups },
    { label: "VOLUNTEERS", value: dashboardCounts.volunteers },
    { label: "TOTAL RECORDS", value: dashboardCounts.total },
  ];

  // 1. Setup a configuration array for your tabs
  const tabsConfig = [
    {
      label: `Email Signups (${emailSignups.length})`,
      component: <EmailSignUpsTab signups={emailSignups} />,
    },
    {
      label: `Volunteers (${volunteers.length})`,
      component: <VolunteersTab volunteers={volunteers} />,
    },
    {
      label: `Voters (${voters.length})`,
      component: <VotersTab voters={voters} />,
    },
    {
      label: `Donations (${donations.length})`,
      component: (
        <DonationsTab donations={donations} summary={donationSummary} />
      ),
    },
    {
      label: `Events (${events.length})`,
      component: (
        <EventsTab eventsData={events} refreshDashboard={refreshDashboard} />
      ),
    },
    {
      label: `News (${newsArticles.length})`,
      component: (
        <NewsTab
          articlesData={newsArticles}
          refreshDashboard={refreshDashboard}
        />
      ),
    },
    {
      label: `Vote Info (${voteInfoBoxes.length})`,
      component: (
        <VoteInfoTab
          voteInfoBoxes={voteInfoBoxes}
          refreshDashboard={refreshDashboard}
        />
      ),
    },
    {
      label: `Family Stories (${familyStories.length})`,
      component: (
        <FamilyStoriesTab
          storiesData={familyStories}
          refreshStories={fetchFamilyStories}
        />
      ),
    },
    {
      label: `Photos (${photoCount})`,
      component: <PhotosTab refreshDashboard={refreshDashboard} />,
    },
  ];

  return (
    <Box sx={{ bgcolor: "#F0F2F5", minHeight: "100vh", pb: 5 }}>
      {/* 1. Header / Navbar */}
      <AppBar position="static" sx={{ bgcolor: "#0F1E3D", boxShadow: "none" }}>
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Campaign Admin Dashboard
          </Typography>
          <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Logged in as: admin@taylor4de.com
            </Typography>
            <Button
              variant="contained"
              sx={{
                bgcolor: "#C4912E",
                "&:hover": { bgcolor: "#A67A26" },
                textTransform: "none",
              }}
              onClick={handleOpenModal}
            >
              Logout
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4 }}>
        {/* 2. Metrics Cards Row - Responsive Implementation */}
        <Grid container spacing={{ xs: 6, sm: 6, md: 3 }} sx={{ mb: 10 }}>
          {metrics.map((item, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
              <Card
                sx={{
                  p: 2,
                  height: "100%", // Ensures cards are same height if labels wrap
                  borderLeft: "5px solid #C4912E",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "#0F1E3D",
                    lineHeight: 1.2,
                  }}
                >
                  {isCountsLoading ? (
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={40}
                      sx={{ transform: "none" }}
                    />
                  ) : (
                    item.value
                  )}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "#999",
                    letterSpacing: "0.5px",
                    mt: 0.5,
                  }}
                >
                  {item.label}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* 3. Chart Section */}
        <SignupTrendsChart
          signups={emailSignups}
          isLoading={isEmailSignupsLoading}
        />

        {/* 4. Tab Navigation Section */}
        <Box
          className="dashboard-tabs-container"
          sx={{ bgcolor: "#0F1E3D", borderRadius: "4px", overflow: "hidden" }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTabs-indicator": { display: "none" },
              "& .MuiTab-root": {
                color: "#fff",
                textTransform: "none",
                minHeight: "48px",
                fontSize: "13px",
                fontWeight: 500,
                px: 3,
                "&.Mui-selected": {
                  bgcolor: "#C4912E",
                  color: "#fff",
                },
              },
            }}
          >
            {tabsConfig.map((tab, index) => (
              <Tab key={index} label={tab.label} />
            ))}
          </Tabs>
        </Box>

        {/* 5. Tab Content Area */}
        <Card
          sx={{
            mt: 0,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            padding: "10px",
          }}
        >
          {/* Dynamically render the component based on the active tab index */}
          {tabsConfig.map((tab, index) => (
            <TabPanel key={index} value={tabValue} index={index}>
              {tab.component}
            </TabPanel>
          ))}
        </Card>

        {/* 6. Surveys Section (standalone, below tabs) */}
        <Box sx={{ mt: 6 }}>
          <Box
            sx={{
              bgcolor: "#0F1E3D",
              borderRadius: "4px 4px 0 0",
              px: 3,
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 700,
                fontSize: 15,
                letterSpacing: 0.5,
              }}
            >
              Smart Survey Responses
            </Typography>
            <Typography
              sx={{
                color: "#C4912E",
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              {dashboardCounts.surveys ?? 0} total
            </Typography>
          </Box>
          <Card
            sx={{
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              padding: "10px",
            }}
          >
            <SurveysTab />
          </Card>
        </Box>
      </Container>
      <ConfirmModal
        open={opendialog}
        handleClose={() => setOpenDialog(false)}
        onConfirm={handleLogout}
        title="Logout"
        message="Are you sure you want to Logout?"
        confirmLabel="Logout"
      />
    </Box>
  );
};

// Helper Component for Tab Panels
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default Dashboard;
